import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table2, ChevronsUpDown, Check, Plus } from 'lucide-react';
import { Table } from '../../../types/domain';
import { PortalPopover } from '../../ui/PortalPopover';
import { Button } from '../../ui/Button';

interface DatastoreSwitcherDropdownProps {
  tables: Table[];
  activeTable: Table | null;
  orgId?: string;
  projId?: string;
  onSelectTable: (table: Table) => void;
}

export const DatastoreSwitcherDropdown: React.FC<DatastoreSwitcherDropdownProps> = ({
  tables,
  activeTable,
  orgId,
  projId,
  onSelectTable,
}) => {
  const [tableOpen, setTableOpen] = useState(false);
  const tableButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  return (
    <div className="relative shrink-0">
      <Button
        ref={tableButtonRef as any}
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => setTableOpen(!tableOpen)}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-cf-surfaceActive font-medium text-cf-textPrimary transition-colors h-auto"
        title="Switch Datastore"
      >
        <Table2 size={13} className="text-cf-orange flex-shrink-0" />
        <span className="max-w-[85px] sm:max-w-[140px] truncate">
          {activeTable?.name || 'Datastores'}
        </span>
        <ChevronsUpDown size={11} className="text-cf-textMuted flex-shrink-0" />
      </Button>

      <PortalPopover
        isOpen={tableOpen}
        onClose={() => setTableOpen(false)}
        triggerRef={tableButtonRef}
        align="left"
        className="w-64 bg-cf-surface border border-cf-border rounded-lg shadow-xl overflow-hidden text-xs"
      >
        <div className="px-3 py-2 text-xs uppercase text-cf-textMuted border-b border-cf-border flex items-center justify-between font-medium">
          <span>Datastores ({tables.length})</span>
        </div>

        <div className="max-h-48 overflow-y-auto">
          {tables.map((t) => {
            const isSelected = t.id === activeTable?.id;
            return (
              <Button
                key={t.id}
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => {
                  onSelectTable(t);
                  setTableOpen(false);
                  navigate(
                    `/organization/${orgId || ''}/projects/${projId || ''}/tables/${t.id}`
                  );
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-cf-surfaceActive h-auto rounded-none ${
                  isSelected ? 'text-cf-orange font-medium bg-cf-surfaceActive/60' : 'text-cf-textPrimary'
                }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  <Table2 size={13} className={isSelected ? 'text-cf-orange flex-shrink-0' : 'text-cf-textMuted flex-shrink-0'} />
                  <span className="truncate">{t.name}</span>
                </div>
                {isSelected && <Check size={12} className="text-cf-orange flex-shrink-0 ml-2" />}
              </Button>
            );
          })}
          {tables.length === 0 && (
            <div className="px-3 py-2.5 text-xs text-cf-textMuted">
              No datastores created yet
            </div>
          )}
        </div>

        <div className="border-t border-cf-border">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => {
              setTableOpen(false);
              window.dispatchEvent(new CustomEvent('ericius:open-create-table'));
            }}
            leftIcon={<Plus size={13} />}
            className="w-full justify-start px-3 py-2 text-cf-orange hover:bg-cf-orange/10 text-xs transition-colors font-medium h-auto rounded-none"
          >
            Create Datastore
          </Button>
        </div>
      </PortalPopover>
    </div>
  );
};
